import { useId, type ReactNode } from "react";

export function Slider(props: {
  label: string; value: number; min: number; max: number; step?: number;
  format?: (v: number) => string; onChange: (v: number) => void; hint?: string; disabled?: boolean;
}) {
  const id = useId();
  const text = props.format ? props.format(props.value) : String(props.value);
  return (
    <div className="slider" title={props.hint}>
      <label htmlFor={id}>{props.label}</label>
      <input
        id={id} type="range" min={props.min} max={props.max} step={props.step ?? 0.01} value={props.value}
        aria-valuetext={text} disabled={props.disabled}
        onChange={(e) => props.onChange(Number(e.currentTarget.value))}
      />
      <output htmlFor={id}>{text}</output>
    </div>
  );
}

export function Toggle(props: { pressed: boolean; onChange: (v: boolean) => void; children: ReactNode; label?: string; className?: string; title?: string }) {
  return (
    <button
      type="button" aria-pressed={props.pressed} aria-label={props.label} title={props.title}
      className={`toggle ${props.pressed ? "on" : ""} ${props.className ?? ""}`}
      onClick={() => props.onChange(!props.pressed)}
    >
      {props.children}
    </button>
  );
}

export function Select<T extends string | number>(props: {
  label: string; value: T; options: readonly { value: T; label: string }[]; onChange: (v: T) => void; hideLabel?: boolean;
}) {
  const id = useId();
  return (
    <span className="select">
      <label htmlFor={id} className={props.hideLabel ? "sr-only" : undefined}>{props.label}</label>
      <select
        id={id} value={String(props.value)}
        onChange={(e) => {
          const opt = props.options.find((o) => String(o.value) === e.currentTarget.value);
          if (opt) props.onChange(opt.value);
        }}
      >
        {props.options.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
      </select>
    </span>
  );
}

export function Panel(props: { title: string; children: ReactNode; actions?: ReactNode; className?: string; id?: string }) {
  const hid = useId();
  return (
    <section className={`panel ${props.className ?? ""}`} aria-labelledby={hid} id={props.id}>
      <header className="panel-head">
        <h2 id={hid}>{props.title}</h2>
        {props.actions && <div className="panel-actions">{props.actions}</div>}
      </header>
      {props.children}
    </section>
  );
}

export const pct = (v: number) => `${Math.round(v * 100)}%`;
export const ms = (sec: number) => (sec < 1 ? `${Math.round(sec * 1000)} ms` : `${sec.toFixed(2)} s`);
export const hz = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)} kHz` : `${Math.round(v)} Hz`);
export const panText = (v: number) => (Math.abs(v) < 0.02 ? "centre" : v < 0 ? `${Math.round(-v * 100)}% left` : `${Math.round(v * 100)}% right`);
